import "@/App.css";
import "@/index.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { PwaUpdateProvider } from "@/lib/PwaUpdateContext";
import UpdatePrompt from "@/components/UpdatePrompt";
import TopBar from "@/components/TopBar";
import AdminHub from "@/components/AdminHub";
import { Login, ForgotPassword, ResetPassword } from "@/pages/Auth";
import Activate from "@/pages/Activate";
import SetPassword from "@/pages/SetPassword";
import SignupWizard from "@/pages/SignupWizard";
import LandingPage from "@/pages/LandingPage";
import { VerifyEmailBanner } from "@/components/VerifyEmailBanner";
import { PendingJoinScreen } from "@/components/PendingJoinScreen";
import Dashboard from "@/pages/Dashboard";
import InspectionDetail from "@/pages/InspectionDetail";
import Settings from "@/pages/Settings";
import InstallApp from "@/components/InstallApp";
import OfflineIndicator from "@/components/OfflineIndicator";
import DailyCheck from "@/components/DailyCheck";
import TrialBanner from "@/components/TrialBanner";
import SupportChat from "@/components/SupportChat";
import GuardianWidget from "@/components/GuardianWidget";
import { installGuardianLog } from "@/lib/guardianLog";
import { usesTracker } from "@/lib/module";
installGuardianLog();
import SupportAccessBanner from "@/components/SupportAccessBanner";
import AccessBanner from "@/components/AccessBanner";
import { Loader2 } from "lucide-react";

// Code-split the rarely-visited pages so the first load only ships the core
// inspection workflow. Each of these downloads on first visit (then cached).
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const KnowledgeManager = lazy(() => import("@/pages/KnowledgeManager"));
const ReferencesManager = lazy(() => import("@/pages/ReferencesManager"));
const ChecklistEditor = lazy(() => import("@/pages/ChecklistEditor"));
const ChecklistBuilder = lazy(() => import("@/pages/ChecklistBuilder"));
const ChecklistBuilderEditor = lazy(() => import("@/pages/ChecklistBuilderEditor"));
const DocumentBuilders = lazy(() => import("@/pages/DocumentBuilders"));
const PhotoReportBuilder = lazy(() => import("@/pages/PhotoReportBuilder"));
const SelectModule = lazy(() => import("@/pages/SelectModule"));
const AIChecklistChat = lazy(() => import("@/pages/AIChecklistChat"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const BillingSuccess = lazy(() => import("@/pages/BillingSuccess"));
const CustomerPortal = lazy(() => import("@/pages/CustomerPortal"));
const HelpCentre = lazy(() => import("@/pages/HelpCentre"));
const AdminSupport = lazy(() => import("@/pages/AdminSupport"));
const GuardianDashboard = lazy(() => import("@/pages/GuardianDashboard"));
const SecurityCentre = lazy(() => import("@/pages/SecurityCentre"));
const AccountManagement = lazy(() => import("@/pages/AccountManagement"));
const Reactivate = lazy(() => import("@/pages/Reactivate"));
const InstallPage = lazy(() => import("@/pages/InstallPage"));
const TeamManagement = lazy(() => import("@/pages/TeamManagement"));
const StatutoryDeadlines = lazy(() => import("@/pages/StatutoryDeadlines"));
const RegulatoryTracker = lazy(() => import("@/pages/RegulatoryTracker"));
const CouncilTemplates = lazy(() => import("@/pages/CouncilTemplates"));
const PlatformOverview = lazy(() => import("@/pages/PlatformOverview"));
const PlatformOrganisations = lazy(() => import("@/pages/PlatformOrganisations"));
const PlatformSubscriptions = lazy(() => import("@/pages/PlatformSubscriptions"));
const PlatformEnquiries = lazy(() => import("@/pages/PlatformEnquiries"));
const PlatformRetained = lazy(() => import("@/pages/PlatformRetained"));
const PlatformModules = lazy(() => import("@/pages/PlatformModules"));
const CouncilEmails = lazy(() => import("@/pages/CouncilEmails"));
const DocumentsBilling = lazy(() => import("@/pages/DocumentsBilling"));
const InvoiceEditor = lazy(() => import("@/pages/InvoiceEditor"));
const BusinessBilling = lazy(() => import("@/pages/BusinessBilling"));
const InvoiceTemplates = lazy(() => import("@/pages/InvoiceTemplates"));
const InvoiceTemplateEditor = lazy(() => import("@/pages/InvoiceTemplateEditor"));
const ContractTemplates = lazy(() => import("@/pages/ContractTemplates"));
const ContractEditor = lazy(() => import("@/pages/ContractEditor"));
const ContractAccept = lazy(() => import("@/pages/ContractAccept"));
const SendDocuments = lazy(() => import("@/pages/SendDocuments"));
const TermsOfService = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.TermsOfService })));
const PrivacyPolicy = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.PrivacyPolicy })));

const PageSpinner = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <Loader2 className="animate-spin text-[#1E1B4B]" />
  </div>
);

const HomeRoute = () => {
  const { user, booting } = useAuth();
  if (booting) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#0B1020" }}><Loader2 className="animate-spin text-slate-500"/></div>;
  if (!user) return <LandingPage />;
  return <RequireAuth><Shell><Dashboard /></Shell></RequireAuth>;
};

const RequireAuth = ({ children }) => {
  const { user, booting } = useAuth();
  const location = useLocation();
  if (booting) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#1E1B4B]"/></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.suspended && !location.pathname.startsWith("/billing") && location.pathname !== "/reactivate")
    return <Navigate to="/reactivate" replace />;
  if (user.pending_join_status && !user.team_owner_id) return <PendingJoinScreen />;
  return children;
};

// Platform Administration is locked to the Platform Owner account (backend-enforced too)
const PlatformOwnerRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user?.is_platform_owner) return <Navigate to="/" replace />;
  return children;
};

// Pool & Spa keeps the full statutory certification workflow; every other
// module gets the lightweight council Regulatory Tracker instead.
const StatutoryOrTracker = () => (usesTracker() ? <RegulatoryTracker /> : <StatutoryDeadlines />);

const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50">
    <div aria-hidden="true" data-testid="brand-watermark"
      className="fixed inset-0 z-0 pointer-events-none flex items-center justify-center">
      <img src="/ch-logo-white-840.png" alt="" draggable="false"
        className="w-[72vmin] max-w-[620px] opacity-10 select-none" />
    </div>
    <div className="relative z-10">
      <SupportAccessBanner />
      <TopBar />
      <VerifyEmailBanner />
      <TrialBanner />
      <AccessBanner />
      <OfflineIndicator />
      <DailyCheck />
      <div className="page-in">{children}</div>
      <InstallApp />
      <SupportChat audience="inspector" />
      <GuardianWidget />
    </div>
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PwaUpdateProvider>
        <Toaster position="top-right" richColors theme="dark" />
        <UpdatePrompt />
        <Suspense fallback={<PageSpinner />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<SignupWizard />} />
          <Route path="/signup" element={<SignupWizard />} />
          <Route path="/home" element={<LandingPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/activate" element={<Activate />} />
          <Route path="/set-password" element={<RequireAuth><SetPassword /></RequireAuth>} />
          <Route path="/landing" element={<Navigate to="/home" replace />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/help" element={<HelpCentre />} />
          <Route path="/install" element={<InstallPage />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/portal/:token" element={<CustomerPortal />} />
          <Route path="/contract/accept/:token" element={<ContractAccept />} />
          <Route path="/reactivate" element={<RequireAuth><Reactivate /></RequireAuth>} />
          <Route path="/billing/success" element={<RequireAuth><Shell><BillingSuccess /></Shell></RequireAuth>} />
          <Route path="/" element={<HomeRoute />} />
          <Route path="/select-module" element={<RequireAuth><SelectModule /></RequireAuth>} />
          <Route path="/inspections/:id" element={<RequireAuth><Shell><InspectionDetail /></Shell></RequireAuth>} />
          <Route path="/inspections/:id/photo-report" element={<RequireAuth><Shell><PhotoReportBuilder /></Shell></RequireAuth>} />
          <Route path="/inspections/:id/audit" element={<RequireAuth><Shell><AuditLog /></Shell></RequireAuth>} />
          <Route path="/knowledge" element={<RequireAuth><Shell><KnowledgeManager /></Shell></RequireAuth>} />
          <Route path="/references" element={<RequireAuth><Shell><ReferencesManager /></Shell></RequireAuth>} />
          <Route path="/checklist-editor" element={<RequireAuth><Shell><ChecklistEditor /></Shell></RequireAuth>} />
          {/* Legacy admin URLs → Platform Administration (owner-only) */}
          <Route path="/admin" element={<Navigate to="/admin/platform" replace />} />
          <Route path="/admin/support" element={<Navigate to="/admin/platform/support" replace />} />
          <Route path="/admin/guardian" element={<Navigate to="/admin/platform/guardian" replace />} />
          <Route path="/admin/security" element={<Navigate to="/admin/platform/security" replace />} />
          <Route path="/admin/accounts" element={<Navigate to="/admin/platform/accounts" replace />} />
          {/* Platform Administration — Platform Owner only */}
          <Route path="/admin/platform" element={<RequireAuth><PlatformOwnerRoute><Shell><AdminHub><PlatformOverview /></AdminHub></Shell></PlatformOwnerRoute></RequireAuth>} />
          <Route path="/admin/platform/organisations" element={<RequireAuth><PlatformOwnerRoute><Shell><AdminHub><PlatformOrganisations /></AdminHub></Shell></PlatformOwnerRoute></RequireAuth>} />
          <Route path="/admin/platform/subscriptions" element={<RequireAuth><PlatformOwnerRoute><Shell><AdminHub><PlatformSubscriptions /></AdminHub></Shell></PlatformOwnerRoute></RequireAuth>} />
          <Route path="/admin/platform/enquiries" element={<RequireAuth><PlatformOwnerRoute><Shell><AdminHub><PlatformEnquiries /></AdminHub></Shell></PlatformOwnerRoute></RequireAuth>} />
          <Route path="/admin/platform/retained" element={<RequireAuth><PlatformOwnerRoute><Shell><AdminHub><PlatformRetained /></AdminHub></Shell></PlatformOwnerRoute></RequireAuth>} />
          <Route path="/admin/platform/modules" element={<RequireAuth><PlatformOwnerRoute><Shell><AdminHub><PlatformModules /></AdminHub></Shell></PlatformOwnerRoute></RequireAuth>} />
          <Route path="/admin/platform/accounts" element={<RequireAuth><PlatformOwnerRoute><Shell><AdminHub><AccountManagement /></AdminHub></Shell></PlatformOwnerRoute></RequireAuth>} />
          <Route path="/admin/platform/support" element={<RequireAuth><PlatformOwnerRoute><Shell><AdminHub><AdminSupport /></AdminHub></Shell></PlatformOwnerRoute></RequireAuth>} />
          <Route path="/admin/platform/guardian" element={<RequireAuth><PlatformOwnerRoute><Shell><AdminHub><GuardianDashboard /></AdminHub></Shell></PlatformOwnerRoute></RequireAuth>} />
          <Route path="/admin/platform/security" element={<RequireAuth><PlatformOwnerRoute><Shell><AdminHub><SecurityCentre /></AdminHub></Shell></PlatformOwnerRoute></RequireAuth>} />
          {/* Org-level tools — part of the everyday app, no admin chrome */}
          <Route path="/admin/templates" element={<RequireAuth><Shell><CouncilTemplates /></Shell></RequireAuth>} />
          <Route path="/admin/documents" element={<RequireAuth><Shell><DocumentBuilders /></Shell></RequireAuth>} />
          <Route path="/admin/builder" element={<RequireAuth><Shell><ChecklistBuilder /></Shell></RequireAuth>} />
          <Route path="/admin/builder/ai-chat" element={<RequireAuth><Shell><AIChecklistChat /></Shell></RequireAuth>} />
          <Route path="/admin/builder/:id" element={<RequireAuth><Shell><ChecklistBuilderEditor /></Shell></RequireAuth>} />
          <Route path="/settings" element={<RequireAuth><Shell><Settings /></Shell></RequireAuth>} />
          <Route path="/settings/business-billing" element={<RequireAuth><Shell><BusinessBilling /></Shell></RequireAuth>} />
          <Route path="/settings/invoice-templates" element={<RequireAuth><Shell><InvoiceTemplates /></Shell></RequireAuth>} />
          <Route path="/settings/invoice-templates/:id" element={<RequireAuth><Shell><InvoiceTemplateEditor /></Shell></RequireAuth>} />
          <Route path="/settings/contract-templates" element={<RequireAuth><Shell><ContractTemplates /></Shell></RequireAuth>} />
          <Route path="/documents-billing" element={<RequireAuth><Shell><DocumentsBilling /></Shell></RequireAuth>} />
          <Route path="/documents-billing/invoice/new" element={<RequireAuth><Shell><InvoiceEditor /></Shell></RequireAuth>} />
          <Route path="/documents-billing/invoice/:id" element={<RequireAuth><Shell><InvoiceEditor /></Shell></RequireAuth>} />
          <Route path="/documents-billing/contract/new" element={<RequireAuth><Shell><ContractEditor /></Shell></RequireAuth>} />
          <Route path="/documents-billing/contract/:id" element={<RequireAuth><Shell><ContractEditor /></Shell></RequireAuth>} />
          <Route path="/documents-billing/send" element={<RequireAuth><Shell><SendDocuments /></Shell></RequireAuth>} />
          <Route path="/settings/council-emails" element={<RequireAuth><Shell><CouncilEmails /></Shell></RequireAuth>} />
          <Route path="/team" element={<RequireAuth><Shell><TeamManagement /></Shell></RequireAuth>} />
          <Route path="/statutory" element={<RequireAuth><Shell><StatutoryOrTracker /></Shell></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        </PwaUpdateProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
